import {
  calculateOrderItemTotalAmount,
  displayName,
  formatter,
  formatWeight,
  getDiscountAmtFromPercentage,
} from "app/main/apps/common/AuraFunctions";

const PRODUCT_FIELDS = [
  { key: "quantity", label: "Quantity" },
  {
    key: "weight",
    label: displayName?.weight || "Weight",
    format: (v) => (v != null && v !== "" ? formatWeight(v) : null),
  },
  { key: "square_feet", label: "Sq.ft" },
  { key: "fob", label: "FOB", price: true, format: (v) => (v != null ? formatter.format(v) : null) },
  {
    key: "unitPrice",
    label: "Unit price",
    price: true,
    format: (v) => (v != null ? formatter.format(v) : null),
  },
  {
    key: "discount",
    label: "Discount",
    price: true,
    // Match the line card, which shows the discount as an amount, not a percent.
    derive: (item) =>
      item?.unitPrice == null
        ? null
        : getDiscountAmtFromPercentage(
            item.discount,
            item.unitPrice,
            item.quantity
          ),
    format: (v) => (v != null ? formatter.format(v) : null),
  },
  { key: "tariff", label: "Tariff", price: true, format: (v) => (v != null ? `${v}%` : null) },
  {
    key: "shipping",
    label: "Shipping",
    price: true,
    format: (v) => (v != null ? formatter.format(v) : null),
  },
  { key: "margin", label: "Margin", price: true, format: (v) => (v != null ? `${v}%` : null) },
  {
    key: "price_per_square_feet",
    label: "Price / sq.ft",
    price: true,
    format: (v) => (v != null ? formatter.format(v) : null),
  },
  { key: "sub_type", label: "Sub type" },
  { key: "polish", label: "Polish" },
  { key: "decedent", label: "Decedent" },
  { key: "customer_provider_number", label: "Reference No" },
  { key: "comments", label: "Comments" },
];

const CHANGE_BADGE = {
  ADDED: "Added",
  UPDATED: "Updated",
  DELETED: "Deleted",
};

const norm = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "number") return String(v);
  return String(v).trim();
};

const valuesEqual = (a, b) => norm(a) === norm(b);

const lineKey = (item) => {
  if (item?.productId) return `id:${item.productId}`;
  return `name:${item?.name || ""}|${item?.sub_type || ""}`;
};

/** Raw value used for equality — derived fields compare on their derived value. */
const rawValue = (field, item) =>
  field.derive ? field.derive(item) : item?.[field.key];

const formatField = (field, value) => {
  if (value == null || value === "") return "—";
  if (field.format) {
    const formatted = field.format(value);
    return formatted == null || formatted === "" ? "—" : formatted;
  }
  return String(value);
};

const lineTotalNum = (item) => {
  if (item?.unitPrice == null) return null;
  const n = Number(
    calculateOrderItemTotalAmount(item.unitPrice, item.quantity, item.discount)
  );
  return Number.isFinite(n) ? n : null;
};

const lineTotal = (item) => {
  const n = lineTotalNum(item);
  return n == null ? null : formatter.format(n);
};

const indexLines = (items = []) => {
  const map = new Map();
  items.forEach((item) => {
    map.set(lineKey(item), item);
  });
  return map;
};

const changeBadge = (item) =>
  CHANGE_BADGE[item?.changeType || item?.change_type] || "Updated";

const docKey = (doc) => {
  if (doc?.fileId != null) return `file:${doc.fileId}`;
  if (doc?.file_id != null) return `file:${doc.file_id}`;
  const name =
    doc?.name ||
    doc?.file_name ||
    doc?.original_name ||
    doc?.files?.filename ||
    doc?.files?.name;
  return name ? `name:${name}` : null;
};

const docLabel = (doc) =>
  doc?.name ||
  doc?.file_name ||
  doc?.original_name ||
  doc?.files?.filename ||
  doc?.files?.name ||
  "Document";

/**
 * Build field rows for a line present on both sides (true field-level compare).
 */
const bothSideRows = (aItem, bItem, fields, hidePriceDetails) => {
  const rows = fields.map((f) => {
    const aRaw = rawValue(f, aItem);
    const bRaw = rawValue(f, bItem);
    // Compare the underlying values: formatting rounds, so 10.001 and 10.004
    // would otherwise both read $10.00 and look unchanged.
    const changed = !valuesEqual(aRaw, bRaw);
    return {
      label: f.label,
      aVal: formatField(f, aRaw),
      bVal: formatField(f, bRaw),
      changed,
      same: !changed,
      kind: "diff",
    };
  });
  if (!hidePriceDetails) {
    const aTotRaw = lineTotalNum(aItem);
    const bTotRaw = lineTotalNum(bItem);
    const changed = !valuesEqual(aTotRaw, bTotRaw);
    rows.push({
      label: "Line total",
      aVal: lineTotal(aItem) || "—",
      bVal: lineTotal(bItem) || "—",
      changed,
      same: !changed,
      kind: "diff",
    });
  }
  return rows;
};

/**
 * One-sided delta: edited in only one revision.
 * Presented as an event (not old→new), so callers should not strike-through.
 */
const oneSideRows = (item, side, fields, hidePriceDetails) => {
  const type = item?.changeType || item?.change_type || "—";
  const rows = [
    {
      label: "Change type",
      aVal: side === "a" ? type : null,
      bVal: side === "b" ? type : null,
      changed: true,
      same: false,
      kind: "event",
    },
  ];
  fields.forEach((f) => {
    const val = formatField(f, rawValue(f, item));
    if (val === "—") return;
    rows.push({
      label: f.label,
      aVal: side === "a" ? val : null,
      bVal: side === "b" ? val : null,
      changed: false,
      same: true,
      kind: "event",
    });
  });
  if (!hidePriceDetails) {
    const tot = lineTotal(item);
    if (tot) {
      rows.push({
        label: "Line total",
        aVal: side === "a" ? tot : null,
        bVal: side === "b" ? tot : null,
        changed: false,
        same: true,
        kind: "event",
      });
    }
  }
  return rows;
};

/**
 * Client-side 1b compare for Change History.
 *
 * Important: each revision's `lineItems` / `documents` are **deltas** for that
 * revision (ADDED / UPDATED / DELETED), not a full order snapshot. Missing from
 * the other revision means "not edited there", not "removed from the order".
 */
export function compareProductRevisions(revA, revB, { hidePriceDetails = false } = {}) {
  const aItems = revA?.lineItems || [];
  const bItems = revB?.lineItems || [];
  const aMap = indexLines(aItems);
  const bMap = indexLines(bItems);
  const keys = Array.from(new Set([...aMap.keys(), ...bMap.keys()]));
  const fields = PRODUCT_FIELDS.filter((f) => !(hidePriceDetails && f.price));

  const blocks = keys.map((key, i) => {
    const aItem = aMap.get(key);
    const bItem = bMap.get(key);
    const name = bItem?.name || aItem?.name || `Line ${i + 1}`;

    if (aItem && bItem) {
      const rows = bothSideRows(aItem, bItem, fields, hidePriceDetails);
      const fieldChanged = rows.some((r) => r.changed);
      return {
        key,
        no: i + 1,
        name,
        badge: fieldChanged ? "Updated" : "Unchanged",
        scope: "both",
        note: null,
        rows,
        hasChanges: fieldChanged,
      };
    }

    if (aItem) {
      return {
        key,
        no: i + 1,
        name,
        badge: changeBadge(aItem),
        scope: "a_only",
        note: "Changed in the left revision only.",
        rows: oneSideRows(aItem, "a", fields, hidePriceDetails),
        hasChanges: true,
      };
    }

    return {
      key,
      no: i + 1,
      name,
      badge: changeBadge(bItem),
      scope: "b_only",
      note: "Changed in the right revision only.",
      rows: oneSideRows(bItem, "b", fields, hidePriceDetails),
      hasChanges: true,
    };
  });

  // Documents are also per-revision deltas with changeType.
  const aDocs = revA?.documents || [];
  const bDocs = revB?.documents || [];
  const aDocMap = new Map();
  const bDocMap = new Map();
  aDocs.forEach((d) => {
    const k = docKey(d);
    if (k) aDocMap.set(k, d);
  });
  bDocs.forEach((d) => {
    const k = docKey(d);
    if (k) bDocMap.set(k, d);
  });
  const docKeys = Array.from(new Set([...aDocMap.keys(), ...bDocMap.keys()]));
  const docRows = docKeys.map((k) => {
    const aDoc = aDocMap.get(k);
    const bDoc = bDocMap.get(k);
    const label = docLabel(bDoc || aDoc);
    if (aDoc && bDoc) {
      const aType = aDoc.changeType || aDoc.change_type || "—";
      const bType = bDoc.changeType || bDoc.change_type || "—";
      return {
        label,
        aVal: aType,
        bVal: bType,
        changed: aType !== bType,
        same: aType === bType,
        kind: "diff",
      };
    }
    if (aDoc) {
      return {
        label,
        aVal: aDoc.changeType || aDoc.change_type || "Changed",
        bVal: null,
        changed: true,
        same: false,
        kind: "event",
        side: "a",
      };
    }
    return {
      label,
      aVal: null,
      bVal: bDoc.changeType || bDoc.change_type || "Changed",
      changed: true,
      same: false,
      kind: "event",
      side: "b",
    };
  });

  // Do not surface a revision-level amount strip here. Change History only
  // carries the lines that changed in each revision, so summing those deltas
  // invents totals that disagree with the order header amount. Field-level
  // "Line total" rows below already show the real per-line price change.
  return {
    blocks,
    hasVasCompare: false,
    vasBlocks: [],
    documents: {
      aCount: aDocs.length,
      bCount: bDocs.length,
      rows: docRows,
      hasChanges: docRows.some((r) => r.changed),
    },
    meta: {
      aTotal: "—",
      bTotal: "—",
      netAmt: "—",
      netWt: "—",
      showAmount: false,
    },
    deltaHint:
      "Change History lists only what changed in each revision. Entries on one side only were not edited in the other revision — not removed from the order.",
  };
}

/**
 * Client-side VAS compare helper.
 * Retained for a future VAS snapshot/compare path — Product Compare no longer
 * mounts VAS (Staff/Admin product-only for PGSS-5186). Do not delete without
 * checking that ticket.
 */
export function compareVasRevisions(revA, revB) {
  const aItems = revA?.vasChanges || [];
  const bItems = revB?.vasChanges || [];
  const keyOf = (item, i) =>
    item?.vas?.id != null ? `vas:${item.vas.id}` : `idx:${i}`;

  const aMap = new Map(aItems.map((item, i) => [keyOf(item, i), item]));
  const bMap = new Map(bItems.map((item, i) => [keyOf(item, i), item]));
  const keys = Array.from(new Set([...aMap.keys(), ...bMap.keys()]));

  const blocks = keys.map((key, i) => {
    const aItem = aMap.get(key);
    const bItem = bMap.get(key);

    if (aItem && bItem) {
      const rows = [
        {
          label: "Change type",
          aVal: aItem?.change_type || aItem?.changeType || "—",
          bVal: bItem?.change_type || bItem?.changeType || "—",
        },
        {
          label: "Updated by",
          aVal: aItem?.updated_by || "—",
          bVal: bItem?.updated_by || "—",
        },
        {
          label: "Updated on",
          aVal: aItem?.updated_at || "—",
          bVal: bItem?.updated_at || "—",
        },
      ].map((r) => ({
        ...r,
        changed: r.aVal !== r.bVal,
        same: r.aVal === r.bVal,
      }));
      const fieldChanged = rows.some((r) => r.changed);
      return {
        key,
        no: i + 1,
        name: `VAS ${i + 1}`,
        badge: fieldChanged ? "Updated" : "Unchanged",
        scope: "both",
        note: null,
        rows,
        hasChanges: fieldChanged,
      };
    }

    const item = aItem || bItem;
    const side = aItem ? "a" : "b";
    return {
      key,
      no: i + 1,
      name: `VAS ${i + 1}`,
      badge: changeBadge(item),
      scope: side === "a" ? "a_only" : "b_only",
      note:
        side === "a"
          ? "Edited in the left revision only."
          : "Edited in the right revision only.",
      rows: [
        {
          label: "Change type",
          aVal: side === "a" ? item?.change_type || item?.changeType || "—" : null,
          bVal: side === "b" ? item?.change_type || item?.changeType || "—" : null,
          changed: true,
          same: false,
          kind: "event",
        },
        {
          label: "Updated by",
          aVal: side === "a" ? item?.updated_by || "—" : null,
          bVal: side === "b" ? item?.updated_by || "—" : null,
          changed: false,
          same: true,
          kind: "event",
        },
        {
          label: "Updated on",
          aVal: side === "a" ? item?.updated_at || "—" : null,
          bVal: side === "b" ? item?.updated_at || "—" : null,
          changed: false,
          same: true,
          kind: "event",
        },
      ],
      hasChanges: true,
    };
  });

  return {
    blocks,
    hasVasCompare: true,
    vasBlocks: blocks,
    documents: { aCount: 0, bCount: 0, rows: [], hasChanges: false },
    meta: { aTotal: "—", bTotal: "—", netAmt: "—", netWt: "—", showAmount: false },
    deltaHint:
      "Change History lists only what changed in each revision. Entries shown on one side only were not edited in the other revision.",
  };
}

/**
 * Approval-round number from a Change History entry (PGSS-5196 / PGSS-5199).
 * Several saves share one round; `rev` identifies the save, this identifies the round.
 */
export function approvalRoundNumber(entry) {
  const n = entry?.revisionNumber ?? entry?.revision_number;
  if (n == null || n === "") return null;
  const num = Number(n);
  return Number.isFinite(num) ? num : null;
}

/**
 * User-facing Change N for a history entry (PGSS-5196 / PGSS-5199).
 *
 * Prefers the API `changeNumber` (assigned server-side after empty-entry suppression).
 * Falls back to `total - index` so labels still work before backend #3995 deploys.
 */
export function changeEntryNumber(index, total, entry) {
  const n = entry?.changeNumber ?? entry?.change_number;
  if (n != null && n !== "") {
    const num = Number(n);
    if (Number.isFinite(num)) return num;
  }
  return total - index;
}

/**
 * User-facing approval-round fragment for a Change History label (rounds ≥ 1).
 * Round `0` is handled separately as "Initial".
 */
export function approvalRoundLabel(round, { short = false } = {}) {
  if (round == null || round === 0) return null;
  return short ? String(round) : `#${round}`;
}

/**
 * Change History label (PGSS-5196 / PGSS-5199).
 *
 * Prefer API `changeNumber` for N; approval round comes from `revisionNumber` — e.g.
 * `Change 1 - Revision #1`, `Change 3 - Initial` (round 0).
 *
 * @param {number} index zero-based index in the newest-first list
 * @param {number} total number of entries
 * @param {object} [entry] Change History row
 * @param {{ short?: boolean }} [options]
 */
export function changeEntryLabel(index, total, entry, options = {}) {
  const changeNo = changeEntryNumber(index, total, entry);
  const round = approvalRoundNumber(entry);
  if (round == null) return `Change ${changeNo}`;
  if (round === 0) {
    return options.short ? `Change ${changeNo} · Initial` : `Change ${changeNo} - Initial`;
  }
  const roundText = approvalRoundLabel(round, { short: options.short });
  if (options.short) {
    return `Change ${changeNo} · Rev ${roundText}`;
  }
  return `Change ${changeNo} - Revision ${roundText}`;
}

/** Envers revision id from a Change History entry (API `fromRev` / `toRev`).
 *  Only `rev` — do not fall back to business/positional revision numbers. */
export function revisionEnversRev(entry) {
  const n = entry?.rev;
  if (n == null || n === "") return null;
  const num = Number(n);
  return Number.isFinite(num) ? num : null;
}

/** Staff/Admin only — matches backend compare auth (Sales is excluded). */
export function canUseChangeHistorySnapshotCompare(user) {
  return Boolean(user && (user.role === "admin" || user.role === "staff"));
}

const formatMoney = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? formatter.format(n) : String(v);
};

const formatSnapshotValue = (key, value) => {
  if (value == null || value === "") return "—";
  switch (key) {
    case "unit_price":
    case "fob":
    case "shipping":
    case "price_per_square_feet":
      return formatMoney(value) ?? "—";
    case "tariff":
    case "margin":
    case "discount_percent":
      return `${value}%`;
    case "weight":
      return formatWeight(value) ?? "—";
    default:
      return String(value);
  }
};

const presenceScope = (presence) => {
  if (presence === "from_only") return "a_only";
  if (presence === "to_only") return "b_only";
  return "both";
};

const presenceBadge = (presence, hasChanged) => {
  if (presence === "from_only") return "Removed";
  if (presence === "to_only") return "Added";
  return hasChanged ? "Updated" : "Unchanged";
};

/**
 * Map PGSS-5186 snapshot compare API payload → ChangeHistoryComparePanel shape.
 * Documents are out of scope for the snapshot endpoint.
 */
export function mapSnapshotCompareResponse(api, { hidePriceDetails = false } = {}) {
  const lines = Array.isArray(api?.lines) ? api.lines : [];
  const blocks = lines.map((line, idx) => {
    const presence = line?.presence || "both";
    const scope = presenceScope(presence);
    const fields = Array.isArray(line?.fields) ? line.fields : [];
    const hasFieldChanges = fields.some((f) => f?.changed);
    const hasChanges =
      presence === "from_only" || presence === "to_only" || hasFieldChanges;
    const isEvent = scope !== "both";

    const rows = fields
      .filter((f) => !(hidePriceDetails && isPriceLikeKey(f?.key)))
      .map((f) => {
        const aRaw = f?.from_value;
        const bRaw = f?.to_value;
        return {
          label: f?.label || f?.key || "—",
          aVal:
            presence === "to_only"
              ? null
              : formatSnapshotValue(f?.key, aRaw),
          bVal:
            presence === "from_only"
              ? null
              : formatSnapshotValue(f?.key, bRaw),
          changed: Boolean(f?.changed),
          same: !f?.changed,
          kind: isEvent ? "event" : "diff",
        };
      });

    return {
      key: `snap:${line?.order_detail_id ?? idx}`,
      no: idx + 1,
      name: line?.name || `Line ${idx + 1}`,
      scope,
      badge: presenceBadge(presence, hasFieldChanges),
      note:
        presence === "from_only"
          ? "Removed between these revisions."
          : presence === "to_only"
            ? "Added between these revisions."
            : null,
      rows,
      hasChanges,
    };
  });

  const aAmt = formatMoney(api?.from_amount);
  const bAmt = formatMoney(api?.to_amount);
  const netAmt = formatMoney(api?.net_amount);
  const showAmount =
    !hidePriceDetails && aAmt != null && bAmt != null;

  return {
    blocks,
    hasVasCompare: false,
    vasBlocks: [],
    documents: { aCount: 0, bCount: 0, rows: [], hasChanges: false },
    meta: {
      aTotal: aAmt ?? "—",
      bTotal: bAmt ?? "—",
      netAmt: netAmt ?? "—",
      netWt:
        api?.net_weight != null && api?.net_weight !== ""
          ? String(api.net_weight)
          : "—",
      showAmount,
    },
    deltaHint: null,
    source: "snapshot",
  };
}

const PRICE_LIKE_KEYS = new Set([
  "unit_price",
  "fob",
  "shipping",
  "price_per_square_feet",
  "tariff",
  "margin",
  "discount_percent",
]);

function isPriceLikeKey(key) {
  return PRICE_LIKE_KEYS.has(key);
}

/**
 * PGSS-5210: re-attach the inlined base64 to a document change list.
 *
 * `added_documents` is the one list the API sends without `data` — it is a subset of `documents`, which
 * already carries it, so duplicating would double an already heavy response. `removed_documents` and
 * `decision_documents` DO carry `data`, because neither is in `documents`: a removed document is by
 * definition the one no longer in the snapshot, and decision documents describe what came back rather than
 * what was sent. Only `added` can be resolved this way.
 *
 * The viewer builds its thumbnail, View and Download from `data` (getFileImageSrc / onFileViwerClick /
 * handleDownload), so a stripped entry renders a broken card with dead actions.
 *
 * Metadata wins on every field except the binary, so an entry keeps the category it was snapshotted with
 * rather than the file's present-day type. An id with no match passes through untouched — better a card
 * without a preview than a crash.
 */
export const withBinaryFrom = (documents, list) => {
  if (!list?.length) return [];
  const byId = new Map((documents || []).map((doc) => [doc?.id, doc]));
  return list.map((doc) => {
    const full = byId.get(doc?.id);
    // PGSS-5505: carry the link as well as the bytes. A document now usually arrives as a signed
    // url with no data at all, and rehydrating only `data` left added-document cards with nothing
    // to render.
    return full ? { ...full, ...doc, data: full.data, url: full.url ?? doc?.url } : doc;
  });
};
